import ConductionModal from "@/components/modals/ConductionModal";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Project } from "@/javascript/project-objects";
import { Utils } from "@/javascript/utils";
import {
    Badge,
    Button,
    Flex,
    Pagination,
    ScrollArea,
    Skeleton,
    Stack,
    Table,
    Text,
} from "@mantine/core";
import { IconAlertCircle, IconFileDescription, IconFilePlus } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

interface ProjectSelectProps {
    userId?: string;
    onClose: () => void;
}

const ProjectSelect: React.FC<ProjectSelectProps> = ({ userId, onClose }) => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [desiredPage, setDesiredPage] = useState<number>(1);
    const [projectsPerPage, setProjectsPerPage] = useState<number>(6);
    const [loadingProjectId, setLoadingProjectId] = useState<string | null>(null);

    const auth = useContext(AuthContext);
    if (auth === undefined) {
        throw new Error("ProjectSelect must be a child of AuthContext.");
    }
    const token = auth.token;
    if (token === null) {
        return;
    }

    useEffect(() => {
        if (!userId) {
            return;
        }

        ConductionAPI.getProjects(userId, token)
            .then((fetchedProjects) => {
                if (fetchedProjects && fetchedProjects.length > 0) {
                    // Sort projects by last edited date (newest first)
                    const sortedProjects = fetchedProjects.sort(
                        (a, b) => new Date(b.getLastEditedDate()).getTime() - new Date(a.getLastEditedDate()).getTime()
                    );
                    setProjects(sortedProjects);
                }
            })
            .catch(() => {
                console.error("Failed to fetch projects.");
                Utils.displayNotification(
                    "Error Loading Projects",
                    "An error occurred while fetching your projects. Please try again.",
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            })
            .finally(() => setLoading(false));
    }, [userId, token]);

    const height = 60; // view height (vh)
    const calculateProjectsPerPage = useCallback(() => {
        const rowHeight = 45; // pixel estimation
        const containerHeight = window.innerHeight * (height / 100) - 100; // Use 60% of the viewport height minus some estimate for other elements
        setProjectsPerPage(Math.floor(containerHeight / rowHeight));

        if (projects && Math.floor(containerHeight / rowHeight) * (desiredPage - 1) + 1 > projects.length) {
            setDesiredPage(1);
        }
    }, [desiredPage, window.innerHeight]);

    useEffect(() => {
        calculateProjectsPerPage();
        window.addEventListener("resize", calculateProjectsPerPage);
        return () => window.removeEventListener("resize", calculateProjectsPerPage);
    }, [calculateProjectsPerPage]);

    const paginatedProjects = useMemo(() => {
        const start = (desiredPage - 1) * projectsPerPage;
        return projects.slice(start, start + projectsPerPage);
    }, [desiredPage, projects, projectsPerPage]);

    const getLatestSubmissionDate = (project: Project): string => {
        if (!project.submissions || project.submissions.length === 0) {
            return "Not Submitted";
        }
        const latestSubmission = [...project.submissions].sort(
            (a, b) => new Date(b.dateSubmitted).getTime() - new Date(a.dateSubmitted).getTime()
        )[0];
        return formatDateString(latestSubmission.dateSubmitted);
    };

    return (
        <ConductionModal
            roles={["User"]}
            title={"Select a Project"}
            onClose={onClose}
            size={"80%"}
        >
            <Stack
                align="center"
                justify="center"
            >
                <Button
                    onClick={async () => {await auth.openProject(); onClose();}}
                >
                    <IconFilePlus size={16} style={{ marginRight: 8 }} />
                    Start a New Project
                </Button>

                <Flex
                    direction={"column"}
                    align="center"
                    justify="space-between"
                    style={{
                        height: `${height}vh`,
                        width: "100%"
                    }}
                >
                    {loading ? (
                        <Skeleton height={"100%"} radius="md" />
                    ) : projects.length === 0 ? (
                        <Text>No projects found.</Text>
                    ) : (
                        <ScrollArea
                            style={{
                                width: "100%"
                            }}
                        >
                            <Table
                                striped
                                stickyHeader
                                highlightOnHover
                            >
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Name</Table.Th>
                                        <Table.Th>Created</Table.Th>
                                        <Table.Th>Last Edited</Table.Th>
                                        <Table.Th>Submitted</Table.Th>
                                        <Table.Th>Actions</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {paginatedProjects.map((project) => (
                                        <Table.Tr key={project.getId()}>
                                            <Table.Td>{project.getName()}</Table.Td>
                                            <Table.Td><Badge color="blue">{formatDateString(project.getCreatedDate())}</Badge></Table.Td>
                                            <Table.Td><Badge color="blue">{formatDateString(project.getLastEditedDate())}</Badge></Table.Td>
                                            <Table.Td><Badge color={project.submissions.length <= 0 ? "red" : "darkGreen"}>{getLatestSubmissionDate(project)}</Badge></Table.Td>
                                            <Table.Td>
                                                <Button
                                                    size="sm"
                                                    onClick={async () => {
                                                        setLoadingProjectId(project.getId());
                                                        await auth.openProject(project.getId());
                                                        setLoadingProjectId(null);
                                                        onClose();
                                                    }}
                                                    loading={loadingProjectId === project.getId()}
                                                    disabled={loadingProjectId !== null && loadingProjectId !== project.getId()}
                                                >
                                                    <IconFileDescription size={16} style={{ marginRight: 8 }} />
                                                    Load Project
                                                </Button>
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    )}

                    {projects.length > projectsPerPage && (
                        <Pagination
                            total={Math.ceil(projects.length / projectsPerPage)}
                            value={desiredPage}
                            onChange={setDesiredPage}
                            style={{
                                paddingTop: 20
                            }}
                        />
                    )}
                </Flex>
            </Stack>
        </ConductionModal>
    );
};

function formatDateString(stringDate: Date): string {
    if (!stringDate) return "Invalid Date";
    const date = new Date(stringDate);
    return date.toLocaleDateString(); // TODO: fix time
}

export default ProjectSelect;
